import { FavoriteButton } from '@liveauctioneers/hammer-ui-core/favoriteButton';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { useCallback, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import ShouldLogInContainer from '@/components/ShouldLogIn/ShouldLogInContainer';
import { useSaveAnimations } from '@/hooks/useSaveAnimations';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { isUserLoggedIn } from '@/redux/modules/account/user/user.selectors';
import { EventContext } from '@/redux/modules/analytics';
import { toggleItemSave } from '@/redux/modules/item/saved/savedItems.actions';
import { isItemSaved } from '@/redux/modules/item/saved/savedItems.selectors';
import { CompleteAccountModalContext, ModalContext } from '@/redux/modules/modal';

type Props = {
    canShowEasyShipping?: boolean;
    /**
     * When true, enables the primary LDP save animations:
     *  - Idle wiggle nudge for first-time savers
     *  - Post-save button pulse, heart pop, and confetti burst
     * Should only be set on the primary LDP Save button (GalleryActions).
     * All secondary item-card instances must omit this prop.
     */
    enableSaveAnimations?: boolean;
    itemId: number;
    labeled?: boolean;
    shouldShouldToolTipExperiment?: boolean;
};

const ItemCardSaveIcon = ({
    canShowEasyShipping = false,
    enableSaveAnimations = false,
    itemId,
    labeled = false,
    shouldShouldToolTipExperiment = false,
}: Props) => {
    const saveButtonId = `saveBtn-${itemId}`;
    const { formatMessage } = useIntl();
    const dispatch = useAppDispatch();
    const location = useLocation();

    const userLoggedIn = useAppSelector(isUserLoggedIn);
    const isFavorite = useAppSelector((state) => isItemSaved(state, itemId));

    const buttonRef = useRef<HTMLButtonElement>(null);

    const { isActivating, isWiggling, onButtonFocus, onButtonMouseEnter } = useSaveAnimations({
        buttonRef,
        enabled: enableSaveAnimations,
        isFavorite,
        itemId,
    });

    const modalContext = useMemo<ModalContext>(
        () => ({
            eventContext: EventContext.saveItem,
            id: itemId,
            itemId,
            type: CompleteAccountModalContext.SeeSoldPrice,
        }),
        [itemId]
    );

    const onToggleSave = useCallback(() => {
        dispatch(toggleItemSave({ canHaveEasyShippingTag: canShowEasyShipping, itemId, location }));
    }, [dispatch, canShowEasyShipping, itemId, location]);

    return (
        <>
            <ShouldLogInContainer
                forceSignUp={!userLoggedIn}
                modalContext={modalContext}
                runFunction={onToggleSave}
            >
                <FavoriteButton
                    aria-label="favorite button"
                    favorited={isFavorite}
                    id={saveButtonId}
                    isActivating={enableSaveAnimations ? isActivating : undefined}
                    isWiggling={enableSaveAnimations ? isWiggling : undefined}
                    onFocus={enableSaveAnimations ? onButtonFocus : undefined}
                    onMouseEnter={enableSaveAnimations ? onButtonMouseEnter : undefined}
                    ref={buttonRef}
                    tooltipProps={{
                        anchorSelect: `#${saveButtonId}`,
                        content: formatMessage({ id: 'save_to_my_items' }),
                        enabled: shouldShouldToolTipExperiment || (!isFavorite && labeled),
                        place: 'top',
                        positionStrategy: 'fixed',
                    }}
                    variant={enableSaveAnimations ? 'labeled-animated' : labeled ? 'labeled' : 'circle'}
                />
            </ShouldLogInContainer>
        </>
    );
};

export default ItemCardSaveIcon;
